if (!customElements.get('product-form')) {
  const replaceCartLineSafely = async (readCart, postCart, originalLine, replacement) => {
    const propertiesKey = (value) => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
    const planId = (line) => String(line.selling_plan_allocation?.selling_plan?.id || line.selling_plan || '');
    const matches = (line, item) => String(line.variant_id || line.id) === String(item.variant_id || item.id)
      && propertiesKey(line.properties) === propertiesKey(item.properties) && planId(line) === planId(item);
    const find = (cart, item) => (cart.items || []).filter(line => matches(line, item));
    const quantity = Number(originalLine.quantity);
    replacement.quantity = quantity;
    if (String(originalLine.variant_id) === String(replacement.id)) {
      return postCart('cart/change.js', {id: originalLine.key, quantity, properties: replacement.properties});
    }
    const before = await readCart();
    const originals = find(before, originalLine);
    if (originals.length !== 1 || originals[0].quantity !== quantity) throw new Error('Your cart changed. Please reopen Edit and try again.');
    const baseline = find(before, replacement).reduce((sum, line) => sum + line.quantity, 0);
    let addError;
    try { await postCart('cart/add.js', {items: [replacement]}); } catch (error) { addError = error; }
    const afterAdd = await readCart();
    const replacements = find(afterAdd, replacement);
    const total = replacements.reduce((sum, line) => sum + line.quantity, 0);
    if (total !== baseline + quantity) throw addError || new Error('Unable to confirm the new size. Your original item has not been removed.');
    const currentOriginals = find(afterAdd, originalLine);
    if (currentOriginals.length !== 1 || currentOriginals[0].quantity !== quantity) throw new Error('Your cart changed during the update. Please review your cart.');
    try {
      return await postCart('cart/change.js', {id: currentOriginals[0].key, quantity: 0});
    } catch (removeError) {
      const latest = await readCart();
      if (!find(latest, originalLine).length) return latest;
      const addedLines = find(latest, replacement);
      const addedTotal = addedLines.reduce((sum, line) => sum + line.quantity, 0);
      if (addedTotal === baseline + quantity) {
        let remaining = quantity;
        for (const line of addedLines) {
          const subtract = Math.min(remaining, line.quantity);
          if (subtract) await postCart('cart/change.js', {id: line.key, quantity: line.quantity - subtract});
          remaining -= subtract;
          if (!remaining) break;
        }
      }
      throw removeError;
    }
  };

  const ensureKalisaAtcFeedbackStyles = () => {
    if (document.getElementById('kalisa-atc-feedback-styles')) return;

    const style = document.createElement('style');
    style.id = 'kalisa-atc-feedback-styles';
    style.textContent = `
      product-form [type="submit"]:not([disabled]):not([aria-disabled="true"]):not(.kalisa-atc--loading):not(.kalisa-atc--added) {
        background: #681F32 !important;
        border-color: #681F32 !important;
        color: #ffffff !important;
        transition: background-color 180ms ease, border-color 180ms ease, color 180ms ease;
      }

      product-form [type="submit"].kalisa-atc--loading {
        background: #681F32 !important;
        border-color: #681F32 !important;
        color: #ffffff !important;
        opacity: 1 !important;
        cursor: wait;
      }

      product-form [type="submit"].kalisa-atc--added {
        background: #F5E8E3 !important;
        border-color: #681F32 !important;
        color: #681F32 !important;
        opacity: 1 !important;
      }

      product-form [type="submit"].kalisa-atc--added::before {
        content: "✓";
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-inline-end: 0.8rem;
        font-size: 1.8rem;
        line-height: 1;
        font-weight: 600;
        color: #681F32;
      }

      product-form [type="submit"].kalisa-atc--loading > span {
        opacity: 1 !important;
        visibility: visible !important;
      }

      product-form [type="submit"].kalisa-atc--loading .loading__spinner {
        position: static !important;
        display: inline-flex !important;
        width: 1.8rem;
        height: 1.8rem;
        margin-inline-start: 0.8rem;
        opacity: 1 !important;
        visibility: visible !important;
      }

      product-form [type="submit"].kalisa-atc--loading .loading__spinner svg {
        animation: kalisa-atc-spin 0.75s linear infinite;
        color: #ffffff !important;
      }

      product-form [type="submit"].kalisa-atc--loading .loading__spinner svg circle,
      product-form [type="submit"].kalisa-atc--loading .loading__spinner svg path {
        stroke: #ffffff !important;
      }

      @keyframes kalisa-atc-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;

    document.head.appendChild(style);
  };

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        ensureKalisaAtcFeedbackStyles();
        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.kalisaSubmitting = false;
        this.cart = null; // Add-to-cart success routes to the full cart page.
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.submitButtonDefaultText = this.submitButtonText?.textContent?.trim() || window.variantStrings.addToCart;

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.setupKalisaCustomSizeEdit();
      }

      getKalisaCustomSizeEditState() {
        try {
          const params = new URLSearchParams(window.location.search);
          if (params.get('kalisa_edit_custom_size') !== '1') return null;
          const raw = sessionStorage.getItem('kalisaCustomSizeCartEdit');
          if (!raw) return null;
          const state = JSON.parse(raw);
          if (!state || state.source !== 'cart' || !state.lineKey) return null;
          return state;
        } catch (_) {
          return null;
        }
      }

      setupKalisaCustomSizeEdit() {
        const state = this.getKalisaCustomSizeEditState();
        if (!state) return;

        this.dataset.kalisaCustomSizeEdit = 'true';
        this.submitButtonDefaultText = 'Update Custom Size';
        if (this.submitButtonText) this.submitButtonText.textContent = 'Update Custom Size';

        const normalize = (value) => String(value || '').trim().toLowerCase();
        const isCustom = (value) => {
          const v = normalize(value);
          return v.includes('custom size') || v.includes('فصلِ مقاسك') || v.includes('مقاس مخصص') || v.includes('مقاس خاص');
        };

        const activateCustomSize = () => {
          const root = this.closest('product-info') || document;
          const radio = Array.from(root.querySelectorAll('input[type="radio"]')).find((el) => isCustom(el.value));
          if (radio) {
            if (!radio.checked) radio.click();
            return true;
          }

          const select = Array.from(root.querySelectorAll('select')).find((el) =>
            Array.from(el.options || []).some((opt) => isCustom(opt.value) || isCustom(opt.textContent))
          );
          if (select) {
            const option = Array.from(select.options).find((opt) => isCustom(opt.value) || isCustom(opt.textContent));
            if (option) {
              if (select.value !== option.value) {
                select.value = option.value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
              }
              return true;
            }
          }
          return false;
        };

        const findMeasurementTarget = () => {
          const root = this.closest('product-info') || document;
          const fields = Array.from(root.querySelectorAll('input, select, textarea'));
          const measurement = fields.find((el) => {
            const hay = normalize([el.name, el.id, el.placeholder, el.getAttribute('aria-label')].filter(Boolean).join(' '));
            return hay.includes('bust') || hay.includes('waist') || hay.includes('hips') ||
              hay.includes('height') || hay.includes('weight') || hay.includes('shoulder') ||
              hay.includes('sleeve') || hay.includes('upper arms') || hay.includes('arm hole') ||
              hay.includes('neck size') || hay.includes('front waist');
          });
          return measurement
            ? (measurement.closest('[class*="tpo"], [class*="custom"], [class*="measurement"], .product-form__input, .field') || measurement)
            : null;
        };

        const root = this.closest('product-info') || document;
        activateCustomSize();

        // Move immediately to the Custom Size option while the measurement app finishes mounting.
        const customOption = Array.from(root.querySelectorAll('input[type="radio"]')).find((el) => isCustom(el.value));
        const customOptionBlock = customOption?.closest('fieldset, .product-form__input');
        if (customOptionBlock) customOptionBlock.scrollIntoView({ behavior: 'auto', block: 'center' });

        const scrollToMeasurements = () => {
          const target = findMeasurementTarget();
          if (!target) return false;
          target.scrollIntoView({ behavior: 'auto', block: 'center' });
          return true;
        };

        if (!scrollToMeasurements()) {
          const observer = new MutationObserver(() => {
            activateCustomSize();
            if (scrollToMeasurements()) observer.disconnect();
          });
          observer.observe(root === document ? document.documentElement : root, {
            childList: true,
            subtree: true
          });

          // Safety stop only; MutationObserver handles the fast path immediately.
          setTimeout(() => observer.disconnect(), 10000);
        }
      }

      async onKalisaCustomSizeEditSubmit(state) {
        if (this.kalisaSubmitting) return;
        this.kalisaSubmitting = true;
        this.handleErrorMessage();
        this.error = false;
        this.setButtonLoadingState();
        if (this.submitButtonText) this.submitButtonText.textContent = 'Updating...';

        const jsonHeaders = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        };

        const postJson = async (url, payload) => {
          const response = await fetch(url, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify(payload)
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || window.cartStrings?.error || 'Unable to update this item.');
          }
          return data;
        };

        let originalLine = null;
        try {
          const cartResponse = await fetch(window.routes?.cart_url + '.js', {
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          });
          const cart = await cartResponse.json();
          originalLine = (cart.items || []).find((item) => String(item.key) === String(state.lineKey));
          if (!originalLine) throw new Error('This cart item changed. Please reopen Edit measurements from the cart.');

          const formData = new FormData(this.form);
          formData.set('quantity', String(Number(state.quantity || originalLine.quantity || 1)));

          const properties = {...(originalLine.properties || {})};
          for (const [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.*)\]$/);
            if (match) properties[match[1]] = value;
          }
          const replacement = {id: Number(formData.get('id')), quantity: originalLine.quantity, properties};
          const sellingPlanId = originalLine.selling_plan_allocation?.selling_plan?.id || state.sellingPlan;
          if (sellingPlanId) replacement.selling_plan = sellingPlanId;
          const cartRoot = window.Shopify?.routes?.root || '/';
          const readCart = async () => {
            const response = await fetch(cartRoot + 'cart.js', {cache:'no-store', headers:{Accept:'application/json'}});
            if (!response.ok) throw new Error('Unable to read cart.');
            return response.json();
          };
          await replaceCartLineSafely(readCart, (path, payload) => postJson(cartRoot + path, payload), originalLine, replacement);

          try { sessionStorage.removeItem('kalisaCustomSizeCartEdit'); } catch (_) {}
          this.setButtonAddedState();
          if (this.submitButtonText) this.submitButtonText.textContent = 'Updated';
          window.location.assign(window.routes?.cart_url || '/cart');
        } catch (error) {
          console.error(error);
          this.error = true;
          this.handleErrorMessage(error?.message || window.cartStrings?.error || 'Unable to update custom size.');
          this.resetButtonFeedbackState();
          if (this.submitButtonText) this.submitButtonText.textContent = 'Update Custom Size';
        } finally {
          this.kalisaSubmitting = false;
        }
      }

      onSubmitHandler(evt) {

        evt.preventDefault();
        if (this.kalisaSubmitting || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const kalisaCustomSizeEditState = this.getKalisaCustomSizeEditState();
        if (kalisaCustomSizeEditState) {
          this.onKalisaCustomSizeEditSubmit(kalisaCustomSizeEditState);
          return;
        }

        this.kalisaSubmitting = true;
        this.handleErrorMessage();
        this.error = false;

        this.setButtonLoadingState();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const data = await response.json();
            if (!response.ok && !data.status) data.status = response.status;
            return data;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.error = true;
              this.handleErrorMessage(typeof response.description === 'string' ? response.description : window.cartStrings.error);

              return;
            } else if (!this.cart) {
              this.setButtonAddedState();
              window.location.assign(window.routes.cart_url);
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            this.setButtonAddedState();
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings.error);
            this.error = true;
          })
          .finally(() => {
            this.kalisaSubmitting = false;
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            if (this.error) {
              this.resetButtonFeedbackState();
              return;
            }

            setTimeout(() => {
              this.resetButtonFeedbackState();
            }, 2500);
          });
      }

      setButtonLoadingState() {
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButtonDefaultText = this.submitButtonText?.textContent?.trim() || this.submitButtonDefaultText;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.remove('kalisa-atc--added');
        this.submitButton.classList.add('loading', 'kalisa-atc--loading');
        if (this.submitButtonText) this.submitButtonText.textContent = 'Adding...';
        this.querySelector('.loading__spinner')?.classList.remove('hidden');
      }

      setButtonAddedState() {
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.classList.remove('loading', 'kalisa-atc--loading');
        this.submitButton.classList.add('kalisa-atc--added');
        if (this.submitButtonText) this.submitButtonText.textContent = 'Added to cart';
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      resetButtonFeedbackState() {
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.classList.remove('loading', 'kalisa-atc--loading', 'kalisa-atc--added');
        const unavailable = this.submitButton.disabled || this.variantIdInput.disabled || !this.variantIdInput.value;
        if (unavailable) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
          if (this.submitButtonText) this.submitButtonText.textContent =
            this.dataset.kalisaCustomSizeEdit === 'true' ? 'Update Custom Size' : this.submitButtonDefaultText;
        }
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButton.getAttribute('aria-busy') !== 'true') this.submitButton.removeAttribute('aria-disabled');
          const editText = this.dataset.kalisaCustomSizeEdit === 'true' ? 'Update Custom Size' : window.variantStrings.addToCart;
          this.submitButtonText.textContent = editText;
          this.submitButtonDefaultText = editText;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

